import React, { useEffect, useState } from 'react';
import SEO from '@/components/layout/SEO';
import { Navbar } from '@/components/layout/Navbar';
import { SiteFooter } from '@/components/layout/SiteFooter';

const launchSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'startzone.dev',
    url: 'https://startzone.dev',
    description: 'startzone.dev is preparing for launch. The next build of the platform is deploying soon.',
});

const TARGET_DATE = new Date(Date.now() + 12 * 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000);

function useCountdown(target: Date) {
    const calc = () => {
        const diff = Math.max(0, target.getTime() - Date.now());
        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            mins: Math.floor((diff / (1000 * 60)) % 60),
            secs: Math.floor((diff / 1000) % 60),
        };
    };
    const [time, setTime] = useState(calc);
    useEffect(() => {
        const id = setInterval(() => setTime(calc()), 1000);
        return () => clearInterval(id);
    }, []);
    return time;
}

function pad(n: number) {
    return String(n).padStart(2, '0');
}

export default function LaunchSoonPage() {
    const { days, hours, mins, secs } = useCountdown(TARGET_DATE);
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#0e0e0e] text-white selection:bg-[#8cacff] selection:text-[#001f55]">
            <SEO schema={launchSchema} title="startzone.dev — Launching Soon" />
            <Navbar />

            <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
                <div className="absolute -left-1/4 top-0 h-full w-full rounded-full bg-[#8cacff]/5 blur-[120px]" />
                <div className="absolute -right-1/4 bottom-0 h-full w-full rounded-full bg-[#d674ff]/5 blur-[120px]" />
                <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(to bottom, transparent 50%, rgba(140, 172, 255, 0.05) 50%)', backgroundSize: '100% 4px' }} />
                <div className="absolute inset-0 opacity-10 mix-blend-screen grayscale">
                    <div className="h-full w-full bg-[radial-gradient(circle_at_20%_80%,rgba(140,172,255,0.18),transparent_25%),radial-gradient(circle_at_80%_20%,rgba(214,116,255,0.12),transparent_20%),linear-gradient(135deg,rgba(255,255,255,0.03),transparent_35%,rgba(255,255,255,0.02)_65%,transparent)]" />
                </div>
                <div className="absolute bottom-0 left-1/2 h-56 w-[900px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.08),transparent_65%)] opacity-30 blur-2xl" />
                <div className="absolute bottom-[-4rem] left-1/2 h-[20rem] w-[46rem] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.1),rgba(255,255,255,0.02)_32%,transparent_70%)] opacity-35 blur-2xl" />
                <div className="absolute bottom-0 left-1/2 h-[18rem] w-[44rem] -translate-x-1/2 opacity-15">
                    <div className="h-full w-full bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.04)_10%,transparent_18%,rgba(255,255,255,0.03)_32%,transparent_40%,rgba(255,255,255,0.04)_55%,transparent_62%,rgba(255,255,255,0.03)_76%,transparent_100%)] [clip-path:polygon(6%_100%,18%_70%,40%_62%,57%_68%,76%_56%,94%_100%)]" />
                </div>
            </div>

            <div className="pointer-events-none fixed left-6 top-1/2 hidden -translate-y-1/2 flex-col gap-8 opacity-40 xl:flex">
                <div className="h-32 w-px bg-gradient-to-b from-transparent via-[#8cacff] to-transparent" />
                <div className="rotate-180 font-['Space_Grotesk'] text-xs tracking-[1em] text-[#484848] [writing-mode:vertical-lr]">
                    SYSTEM_CORE_ONLINE
                </div>
                <div className="h-32 w-px bg-gradient-to-b from-transparent via-[#8cacff] to-transparent" />
            </div>
            <div className="pointer-events-none fixed right-6 top-1/2 hidden -translate-y-1/2 flex-col gap-8 opacity-40 xl:flex">
                <div className="h-32 w-px bg-gradient-to-b from-transparent via-[#00cffc] to-transparent" />
                <div className="font-['Space_Grotesk'] text-xs tracking-[1em] text-[#484848] [writing-mode:vertical-lr]">
                    ACCESS_PROTOCOL_ALPHA
                </div>
                <div className="h-32 w-px bg-gradient-to-b from-transparent via-[#00cffc] to-transparent" />
            </div>

            <main className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-20 pt-28">
                <div className="mb-12 flex flex-col items-center text-center">
                    <div className="mb-5 flex items-center gap-3 border border-[#484848]/20 bg-[#131313] px-4 py-1">
                        <span className="h-2 w-2 animate-pulse bg-[#00cffc]" />
                        <span className="font-['Space_Grotesk'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#00cffc]">
                            INCOMING_TRANSMISSION
                        </span>
                        <span className="font-['Space_Grotesk'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#484848]">//</span>
                        <span className="font-['Space_Grotesk'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#8cacff]">
                            DEPLOYMENT_SEQUENCE_INITIATED
                        </span>
                    </div>
                    <h1 className="max-w-[72rem] font-['Space_Grotesk'] text-5xl font-black uppercase tracking-[-0.06em] text-white md:text-[5.4rem] md:leading-[0.96]">
                        The next evolution of <span className="italic text-[#8cacff]">startzone.dev</span> is loading.
                    </h1>
                </div>

                <div className="mb-14 grid w-full max-w-[48rem] grid-cols-2 gap-6 md:max-w-[50rem] md:grid-cols-4">
                    {[
                        { label: 'DAYS', value: pad(days), text: 'text-[#8cacff]', corner: 'top-left', accent: 'bg-[#8cacff]/20' },
                        { label: 'HOURS', value: pad(hours), text: 'text-[#8cacff]', corner: '', accent: 'bg-[#8cacff]/20' },
                        { label: 'MINS', value: pad(mins), text: 'text-[#00cffc]', corner: '', accent: 'bg-[#00cffc]/20' },
                        { label: 'SECS', value: pad(secs), text: 'text-[#00cffc]', corner: 'bottom-right', accent: 'bg-[#00cffc]/20' },
                    ].map((unit) => (
                        <div key={unit.label} className="group relative min-h-[7.75rem] overflow-hidden bg-[#131313] p-6 text-center md:min-h-[8rem]">
                            {unit.corner === 'top-left' ? <div className="absolute left-0 top-0 h-4 w-4 border-l border-t border-[#8cacff]/40" /> : null}
                            {unit.corner === 'bottom-right' ? <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-[#00cffc]/40" /> : null}
                            <div className={`mb-2 font-['Space_Grotesk'] text-[4rem] font-bold leading-none md:text-[4.4rem] ${unit.text}`}>{unit.value}</div>
                            <div className="font-['Space_Grotesk'] text-[11px] font-medium uppercase tracking-[0.48em] text-[#484848]">{unit.label}</div>
                            <div className={`absolute inset-x-0 bottom-0 h-1 ${unit.accent} transition-colors group-hover:bg-current`} />
                        </div>
                    ))}
                </div>

                <div className="relative w-full max-w-[32rem] border border-[#484848]/10 bg-[#1f2020] p-1 shadow-2xl">
                    <div className="flex items-center gap-2 border-b border-[#484848]/10 bg-[#252626] px-4 py-2">
                        <div className="h-3 w-3 rounded-full bg-[#ff716c]/40" />
                        <div className="h-3 w-3 rounded-full bg-[#00cffc]/40" />
                        <div className="h-3 w-3 rounded-full bg-[#8cacff]/40" />
                        <div className="ml-2 font-['Space_Grotesk'] text-[10px] font-bold uppercase tracking-widest text-[#484848]">
                            SYSTEM_PROMPT: NOTIFY_ME
                        </div>
                    </div>

                    <div className="p-6 md:p-7">
                        <div className="flex flex-col items-stretch gap-4 md:flex-row">
                            <div className="relative flex-1">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-[#8cacff]">&gt;</div>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="ENTER_EMAIL_ADDRESS"
                                className="w-full border-none bg-black py-4 pl-10 font-['Space_Grotesk'] text-sm uppercase tracking-[0.22em] text-[#8cacff] placeholder:text-[#484848]/40 focus:outline-none"
                                    aria-label="Email address"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!email) return;
                                    setSubmitted(true);
                                }}
                                className="bg-gradient-to-r from-[#8cacff] to-[#769dff] px-8 py-4 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.2em] text-[#001f55] shadow-[0_0_15px_rgba(140,172,255,0.4)] transition-all hover:brightness-110 active:scale-95 md:min-w-[10rem]"
                            >
                                EXECUTE_RESERVE
                            </button>
                        </div>
                        {submitted ? (
                            <div className="mt-4 font-mono text-sm tracking-wider text-[#00cffc]">
                                ACK_RECEIVED // You will be notified at deployment.
                            </div>
                        ) : null}
                        <div className="mt-4 flex items-center gap-4">
                            <div className="h-px flex-1 bg-[#484848]/20" />
                            <span className="font-['Space_Grotesk'] text-[9px] uppercase tracking-widest text-[#484848]">
                                ENCRYPTION: AES_256_ENABLED
                            </span>
                            <div className="h-px flex-1 bg-[#484848]/20" />
                        </div>
                    </div>
                </div>

                <div className="mt-16 flex flex-wrap justify-center gap-10 md:gap-12">
                    <div className="flex flex-col gap-1">
                        <span className="font-['Space_Grotesk'] text-[10px] uppercase tracking-widest text-[#484848]">BUILD_HASH</span>
                        <span className="font-mono text-[11px] text-white">0x7F4A_B209_C8D1</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="font-['Space_Grotesk'] text-[10px] uppercase tracking-widest text-[#484848]">UPLINK_STATUS</span>
                        <span className="font-mono text-[11px] uppercase text-[#00cffc] [text-shadow:0_0_10px_rgba(0,207,252,0.5)]">STABLE // 99.9%</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="font-['Space_Grotesk'] text-[10px] uppercase tracking-widest text-[#484848]">GEOLOCATION</span>
                        <span className="font-mono text-[11px] text-white">ZONE_01_REDUX</span>
                    </div>
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}
